import type { InvoicePayment, User, UserBalances } from 'payments-api/model';
import { usePostPaymentFlows } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import { CreatePaymentFlowRequestType } from 'payments-api/model/createPaymentFlowRequestType';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { LineDivider } from '@fragment/ui/src/components/LineDivider/LineDivider';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { PerUserSummaryCurrencyRow } from './InvoiceSummaryTab';
import type { ItemTableItem } from './ItemTable';
import {
  type Direction,
  PAYIN_ARROW,
  PAYOUT_ARROW,
} from './DirectionArrowCells';
import { FormatBillingAmount } from './formatBillingAmount';
import { buildSummaryUserCurrencyRowAmounts } from './InvoiceSummaryTab';
import { PaymentsModal } from './PaymentsModal';
import { UserColorDot } from './UserColorDot';

type PreviewRowProps = {
  date: string;
  userExternalId: string;
  remainingAmount: number;
  currency: CurrencyCode;
  type: Direction;
  mode: 'ACH' | 'WIRE';
  onModeChange?: (mode: 'ACH' | 'WIRE') => void;
};

const PreviewRow = ({
  date,
  userExternalId,
  remainingAmount,
  currency,
  type,
  mode,
  onModeChange,
}: PreviewRowProps) => (
  <div className="flex flex-row items-center justify-between text-xmd h-f3">
    <div className="flex flex-row items-center gap-x-f1 justify-start">
      <span className="flex items-center justify-center aspect-square w-[2ch] font-FragmentMono text-[16px] leading-[1px] text-main-500 border-[0.5px] border-main-200 rounded-1 ">
        {type === 'payin' ? PAYIN_ARROW : PAYOUT_ARROW}
      </span>
      <span className="pr-f1">{date}</span>
      <UserColorDot seed={userExternalId} />
      <span>{userExternalId}</span>
    </div>
    <div className="flex flex-row items-center gap-x-f2 justify-end">
      <FormatBillingAmount amount={remainingAmount} currencyCode={currency} />
      {onModeChange ? (
        <Dropdown
          displayValue={mode}
          placeholder="Select mode"
          options={[
            { value: 'ACH', label: 'ACH' },
            { value: 'WIRE', label: 'WIRE' },
          ]}
          onChange={(value) => onModeChange(value as 'ACH' | 'WIRE')}
          size="xmd"
          width="w-[8ch]"
          padding="px-[8px]"
          height="h-fit"
          align="left"
          data-testid="payment-mode-dropdown"
          border
          borderColor="border-main-200"
          backgroundColor="bg-main-200"
        />
      ) : (
        <span className="text-main-500 border-[0.5px] border-main-200 rounded-1 px-[8px] py-[2px]">
          {mode}
        </span>
      )}
    </div>
  </div>
);

type PreviewTableProps = {
  rows: PerUserSummaryCurrencyRow[];
  stage: 'edit' | 'confirm';
};
const PreviewTable = ({ rows, stage }: PreviewTableProps) => {
  const now = new Date();
  const tPlus2 = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
  const [modes, setModes] = useState<Record<string, 'ACH' | 'WIRE'>>({});
  const [payInRows, payOutRows] = ['payin', 'payout'].map((direction) =>
    rows
      .filter((row) => row.direction === direction)
      .map((row) => (
        <PreviewRow
          key={`${row.userExternalId}-${row.currency}`}
          userExternalId={row.userExternalId}
          remainingAmount={row.remaining}
          currency={row.currency}
          type={row.direction}
          mode={modes[row.userExternalId] ?? 'ACH'}
          date={
            direction === 'payin'
              ? now.toDateString().substring(4)
              : tPlus2.toDateString().substring(4)
          }
          onModeChange={
            stage === 'edit'
              ? (mode) => {
                  setModes((prev: Record<string, 'ACH' | 'WIRE'>) => ({
                    ...prev,
                    [row.userExternalId]: mode,
                  }));
                }
              : undefined
          }
        />
      ))
  );
  if (rows.length === 0) {
    return (
      <div className="flex flex-col">
        <span className="text-xmd text-main-500 pb-f1 pt-f2">
          No payments can be made. All users have zero balance.
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <span className="text-xmd text-main-500 pt-f2">Timeline</span>
      <LineDivider size="xmd" color="gray200" />
      {payInRows}
      <div className="h-fit pl-[1ch] flex items-center py-[0.25lh]">
        <div className="flex border-l-[0.5px] border-main-200 h-f4 items-center pl-[1ch] text-main-500 text-xmd">
          +2 Days
        </div>
      </div>
      {payOutRows}
    </div>
  );
};

type Props = {
  invoiceId: string;
  users: User[];
  userBalances: UserBalances[];
  items: ItemTableItem[];
  payments: InvoicePayment[];
  onClose: () => void;
};

export const PaymentConfirmationModal = ({
  invoiceId,
  users,
  userBalances,
  items,
  payments,
  onClose,
}: Props): JSX.Element => {
  const { showToast } = useToast();
  const [modalStage, setModalStage] = useState<'edit' | 'confirm'>('edit');
  const queryClient = useQueryClient();
  const createFlow = usePostPaymentFlows();

  const handleConfirm = () => {
    const externalId = `paymentflow-${Date.now()}`;
    createFlow.mutate(
      {
        data: {
          type: CreatePaymentFlowRequestType.single_invoice_settlement,
          invoice: { id: invoiceId },
          external_id: externalId,
        },
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['/payment-flows'] });
          showToast(<Toast type="success" message="Payment initiated" />);
          onClose();
        },
        onError: (err) => {
          const message =
            err instanceof Error ? err.message : 'Failed to initiate payment.';
          showToast(<Toast type="error" message={message} />);
        },
      }
    );
  };

  const rows = userBalances
    .map((ub) => buildSummaryUserCurrencyRowAmounts(ub, items, payments, users))
    .flat()
    .filter((row) => row.remaining > 0);

  let buttonText = modalStage === 'edit' ? 'Continue' : 'Confirm';
  if (createFlow.isPending) {
    buttonText = 'Processing...';
  }
  return (
    <PaymentsModal
      onClose={onClose}
      width="sm"
      shouldCenter={false}
      body={
        <div
          className="text-xmd flex flex-col font-helvetica gap-y-f2"
          data-testid="payment-confirmation-modal"
        >
          <div className="flex flex-row items-center justify-between">
            <span className="text-xmd text-main">
              {modalStage === 'edit' ? 'Payment Details' : 'Confirm Payment'}
            </span>
            <span className="text-xmd text-main-500">{invoiceId}</span>
          </div>
          <PreviewTable rows={rows} stage={modalStage} />

          <div className="flex flex-row gap-x-[0.25lh]">
            <SecondaryButtonV3
              onClick={
                modalStage === 'edit' ? onClose : () => setModalStage('edit')
              }
              className="!text-xmd"
            >
              {modalStage === 'edit' ? 'Cancel' : 'Back'}
            </SecondaryButtonV3>
            <PrimaryButtonV3
              onClick={
                modalStage === 'edit'
                  ? () => setModalStage('confirm')
                  : handleConfirm
              }
              disabled={createFlow.isPending || rows.length === 0}
              data-testid="payment-confirmation-confirm-button"
            >
              {buttonText}
            </PrimaryButtonV3>
          </div>
        </div>
      }
    />
  );
};
